import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { oidcConfig } from "@/config";
import { decodeJwtPayload } from "@/lib/jwt";
import { generatePkcePair } from "@/lib/pkce";

const PKCE_VERIFIER_KEY = "tendra.pkce_verifier";
const OAUTH_STATE_KEY = "tendra.oauth_state";
const RETURN_PATH_KEY = "tendra.return_path";
const SILENT_RENEW_FAILED_KEY = "tendra.silent_renew_failed";
const RATE_LIMITED_KEY = "tendra.renew_rate_limited";
// An explicit sign-out isn't an expired session, so skip silent renewal and report it.
const SIGNED_OUT_KEY = "tendra.signed_out";

export interface AuthUser {
  id: string | null;
  email: string | null;
  name: string | null;
  avatarId: string | null;
  roles: string[];
  orgId: string | null;
  orgName: string | null;
  orgSlug: string | null;
}

interface IdTokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  avatar_id?: string;
  role?: string | string[];
  org_id?: string;
  org_name?: string;
  org_slug?: string;
}

function parseUser(idToken: string): AuthUser {
  const claims = decodeJwtPayload<IdTokenClaims>(idToken);
  const roles = claims.role
    ? Array.isArray(claims.role)
      ? claims.role
      : [claims.role]
    : [];

  return {
    id: claims.sub ?? null,
    email: claims.email ?? null,
    // Some accounts carry an empty name claim rather than none at all.
    name: claims.name?.trim() ? claims.name.trim() : null,
    avatarId: claims.avatar_id ?? null,
    roles,
    orgId: claims.org_id ?? null,
    orgName: claims.org_name ?? null,
    orgSlug: claims.org_slug ?? null,
  };
}

interface AuthContextValue {
  accessToken: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  /** A silent renew already failed, so signing in again must be deliberate. */
  renewFailed: boolean;
  /** The renew failed because the auth rate limit was exhausted. */
  rateLimited: boolean;
  /** The user signed out in this tab and has not signed in again. */
  signedOut: boolean;
  login: (returnPath?: string) => Promise<void>;
  logout: () => void;
  completeLogin: (code: string) => Promise<void>;
  markSilentRenewFailed: () => void;
  /**
   * The name comes from an id token claim, so a change made in the console is
   * only picked up on the next renew. Patch it here so the console agrees with
   * what was just saved.
   */
  setUserName: (name: string) => void;
  setUserAvatarId: (avatarId: string | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

let isRedirectingToAuthorize = false;

async function redirectToAuthorize(
  prompt?: "none",
  returnPath = window.location.pathname + window.location.search,
) {
  if (isRedirectingToAuthorize) {
    return;
  }

  isRedirectingToAuthorize = true;

  const { verifier, challenge } = await generatePkcePair();
  const state = crypto.randomUUID();

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(RETURN_PATH_KEY, returnPath);

  const params = new URLSearchParams({
    client_id: oidcConfig.clientId,
    response_type: "code",
    redirect_uri: oidcConfig.redirectUri,
    scope: oidcConfig.scope,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });

  if (prompt) {
    params.set("prompt", prompt);
  }

  window.location.href = `${oidcConfig.authority}/connect/authorize?${params.toString()}`;
}

export function takeOAuthState(): {
  expectedState: string | null;
  returnPath: string | null;
} {
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);

  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);

  return { expectedState, returnPath };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rateLimited, setRateLimited] = useState(() => {
    try {
      return sessionStorage.getItem(RATE_LIMITED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [renewFailed, setRenewFailed] = useState(() => {
    try {
      return sessionStorage.getItem(SILENT_RENEW_FAILED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [signedOut] = useState(() => {
    try {
      return sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
    } catch {
      return false;
    }
  });
  const hasAttemptedSilentRenew = useRef(false);

  useEffect(() => {
    // The back-forward cache can restore this page mid-redirect, leaving the
    // redirect guard set with no redirect to complete. Reload to start cleanly.
    const reloadIfRestoredMidRedirect = (event: PageTransitionEvent) => {
      if (event.persisted && isRedirectingToAuthorize) {
        window.location.reload();
      }
    };

    window.addEventListener("pageshow", reloadIfRestoredMidRedirect);

    return () =>
      window.removeEventListener("pageshow", reloadIfRestoredMidRedirect);
  }, []);

  useEffect(() => {
    if (hasAttemptedSilentRenew.current) {
      return;
    }

    hasAttemptedSilentRenew.current = true;

    // The Login route already starts an interactive sign-in, so starting a silent
    // sign-in here would only race with it.
    if (window.location.pathname === "/login") {
      return;
    }

    const onCallbackRoute = window.location.pathname === "/callback";
    const alreadyFailed =
      sessionStorage.getItem(SILENT_RENEW_FAILED_KEY) === "1" ||
      sessionStorage.getItem(SIGNED_OUT_KEY) === "1";

    if (onCallbackRoute || alreadyFailed) {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsLoading(false);

      return;
    }

    void redirectToAuthorize("none");
  }, []);

  const completeLogin = useCallback(async (code: string) => {
    try {
      const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);

      if (!verifier) {
        throw new Error("Missing PKCE verifier for token exchange.");
      }

      const response = await fetch(`${oidcConfig.authority}/connect/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          code,
          redirect_uri: oidcConfig.redirectUri,
          client_id: oidcConfig.clientId,
          code_verifier: verifier,
        }),
      });

      if (!response.ok) {
        // Every reload spends one token exchange, so refreshing repeatedly can
        // exhaust the auth rate limit. That is transient, not an expired
        // session, and saying so keeps people from re-signing in pointlessly.
        if (response.status === 429) {
          sessionStorage.setItem(RATE_LIMITED_KEY, "1");
          setRateLimited(true);
        }

        throw new Error("Token exchange failed.");
      }

      const data = (await response.json()) as {
        access_token: string;
        id_token?: string;
      };

      setAccessToken(data.access_token);
      setIdToken(data.id_token ?? null);
      setUser(data.id_token ? parseUser(data.id_token) : null);
      sessionStorage.removeItem(SILENT_RENEW_FAILED_KEY);
      sessionStorage.removeItem(RATE_LIMITED_KEY);
      sessionStorage.removeItem(SIGNED_OUT_KEY);
      setRenewFailed(false);
      setRateLimited(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const markSilentRenewFailed = useCallback(() => {
    sessionStorage.setItem(SILENT_RENEW_FAILED_KEY, "1");
    setRenewFailed(true);
    setIsLoading(false);
  }, []);

  const login = useCallback(async (returnPath?: string) => {
    // No state updates here: the page is navigating away, and re-rendering
    // would rebuild useApi and re-fire every data effect into a request the
    // browser is about to abort.
    sessionStorage.removeItem(SILENT_RENEW_FAILED_KEY);
    sessionStorage.removeItem(RATE_LIMITED_KEY);
    sessionStorage.removeItem(SIGNED_OUT_KEY);
    await redirectToAuthorize(undefined, returnPath);
  }, []);

  const logout = useCallback(() => {
    const params = new URLSearchParams({
      post_logout_redirect_uri: oidcConfig.postLogoutRedirectUri,
    });

    if (idToken) {
      params.set("id_token_hint", idToken);
    }

    sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    window.location.href = `${oidcConfig.authority}/connect/logout?${params.toString()}`;
  }, [idToken]);

  const setUserName = useCallback((name: string) => {
    setUser((current) => (current ? { ...current, name } : current));
  }, []);

  const setUserAvatarId = useCallback((avatarId: string | null) => {
    setUser((current) => (current ? { ...current, avatarId } : current));
  }, []);

  const value = useMemo(
    () => ({
      accessToken,
      user,
      isLoading,
      renewFailed,
      rateLimited,
      signedOut,
      login,
      logout,
      completeLogin,
      markSilentRenewFailed,
      setUserName,
      setUserAvatarId,
    }),
    [
      accessToken,
      user,
      isLoading,
      renewFailed,
      rateLimited,
      signedOut,
      login,
      logout,
      completeLogin,
      markSilentRenewFailed,
      setUserName,
      setUserAvatarId,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
